/* ===========================================================
   Estado de demonstração (trial, usuário, equipe, integrações).
   Navegação agora vive na URL — aqui ficam só os "knobs" da demo,
   persistidos em localStorage com migração do formato antigo.
   =========================================================== */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { setActiveTenant } from "../api/client";
import { DEMO_TENANT, DEMO_LEAD_TENANT, buildDemoPersonaEntry, setDemoPersona } from "../mocks/tenants";

export type Trial = "active" | "lastday" | "expired" | "subscribed";

/** Fase do fluxo guiado da demo (ADR §38 / jornada nova) — sobreposto ao app.
 *  gate → preparando → tour → oferta → livre. Estado EM MEMÓRIA (não persiste):
 *  cada visita recomeça. "livre" = sem overlay (explora à vontade). */
export type DemoFase = "gate" | "preparando" | "tour" | "oferta" | "livre";

/** De onde a oferta foi disparada (analytics: `conversao_origem`). EM MEMÓRIA. */
export type OfertaOrigem = "tour" | "banner" | "exit_intent" | null;

export interface DemoUser {
  nome: string;
  email: string;
}

/** Memória do lead entre visitas (pré-fill na 2ª+ interação). Sobrevive ao
 *  `sairDaDemo` (ao contrário de `persona`, que é o escopo runtime). null = lead
 *  ainda não digitou nada (1ª interação → formulários em branco). */
export interface LeadMemo {
  loja: string;
  faturamento: string;
  nome: string;
  email: string;
  whatsapp: string;
}

export interface TeamMember {
  id: number;
  status: "pendente" | "ativo";
  nome: string;
  email: string;
  papel: string;
}

/** Persona do lead na demo personalizada (gate /demo). Troca o nome da loja
 *  em todo o app e escala os números ao faturamento informado (sizeFactor). */
export interface DemoPersona {
  loja: string;
  /** Faixa informada no gate (texto) — para CRM/exibição. */
  faturamento: string;
  /** Fator de escala da vitrine sintética (vs. loja-demo base). */
  sizeFactor: number;
  ativa: boolean;
}

interface DemoState {
  trial: Trial;
  user: DemoUser;
  team: TeamMember[];
  mediaConn: boolean;
  /** Persona do lead; null fora da demo personalizada. */
  persona: DemoPersona | null;
  /** Memória do lead p/ pré-fill entre visitas; null = nunca digitou. */
  leadMemo: LeadMemo | null;
  /** Quantas vezes o gate da demo foi concluído (medir reincidência). */
  demoRuns: number;
  /** Já preencheu a oferta (garantiu a vaga) em alguma visita. */
  leadCapturado: boolean;
}

interface DemoContextValue extends DemoState {
  setTrial: (t: Trial) => void;
  setUser: (u: DemoUser | ((u: DemoUser) => DemoUser)) => void;
  setTeam: (t: TeamMember[] | ((t: TeamMember[]) => TeamMember[])) => void;
  setMediaConn: (v: boolean) => void;
  /** Liga/desliga a persona: re-escopa o tenant ativo + persiste. */
  setPersona: (p: DemoPersona | null) => void;
  /** Fase do fluxo guiado (gate→…→livre). Em memória. */
  demoFase: DemoFase;
  setDemoFase: (f: DemoFase) => void;
  /** De onde a oferta foi aberta (tour/banner/exit_intent) — p/ `conversao_origem`. */
  ofertaOrigem: OfertaOrigem;
  setOfertaOrigem: (o: OfertaOrigem) => void;
  /** Memória do lead p/ pré-fill (null na 1ª interação). */
  leadMemo: LeadMemo | null;
  /** Registra o início de uma run da demo (gate concluído): incrementa o
   *  contador, faz merge de loja/faturamento no leadMemo e devolve o nº da run. */
  registrarRun: (dados: { loja: string; faturamento: string }) => number;
  /** Registra a captura do lead (oferta enviada): merge no leadMemo + flag. */
  registrarLead: (dados: { nome: string; email: string; whatsapp: string }) => void;
  /** Lead já garantiu a vaga em alguma visita. */
  leadCapturado: boolean;
  resetDemo: () => void;
}

const LS_KEY = "lucraa_demo_v1";
const LEGACY_KEY = "lucraa_state_v1"; // formato antigo: incluía screen/tab

const DEFAULTS: DemoState = {
  trial: "active",
  user: { nome: "Camila Souza", email: "camila@koloniskateshop.com" },
  team: [],
  mediaConn: true,
  persona: null,
  leadMemo: null,
  demoRuns: 0,
  leadCapturado: false,
};

function loadInitial(): DemoState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    // migração única: aproveita os knobs do formato antigo e descarta screen/tab
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const { trial, user, team, mediaConn } = JSON.parse(legacy);
      localStorage.removeItem(LEGACY_KEY);
      return {
        ...DEFAULTS,
        ...(trial && { trial }),
        ...(user && { user }),
        ...(team && { team }),
        ...(mediaConn !== undefined && { mediaConn }),
      };
    }
  } catch {
    /* estado corrompido → defaults */
  }
  return DEFAULTS;
}

const DemoContext = createContext<DemoContextValue | null>(null);

export function DemoProvider({ children }: { children: ReactNode }) {
  const [init] = useState(loadInitial);
  const [trial, setTrial] = useState<Trial>(init.trial);
  const [user, setUser] = useState<DemoUser>(init.user);
  const [team, setTeam] = useState<TeamMember[]>(init.team);
  const [mediaConn, setMediaConn] = useState<boolean>(init.mediaConn);
  const [persona, setPersonaState] = useState<DemoPersona | null>(init.persona);
  const [leadMemo, setLeadMemo] = useState<LeadMemo | null>(init.leadMemo);
  const [demoRuns, setDemoRuns] = useState<number>(init.demoRuns);
  const [leadCapturado, setLeadCapturado] = useState<boolean>(init.leadCapturado);
  // Fase do fluxo guiado (em memória): persona já ativa (visita anterior) → "livre".
  const [demoFase, setDemoFase] = useState<DemoFase>(init.persona?.ativa ? "livre" : "gate");
  const [ofertaOrigem, setOfertaOrigem] = useState<OfertaOrigem>(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ trial, user, team, mediaConn, persona, leadMemo, demoRuns, leadCapturado }),
      );
    } catch {
      /* storage indisponível — demo segue sem persistir */
    }
  }, [trial, user, team, mediaConn, persona, leadMemo, demoRuns, leadCapturado]);

  /** Início de uma run (gate concluído): incrementa o contador e guarda
   *  loja/faturamento p/ pré-fill futuro. Devolve o nº desta run (1 na 1ª). */
  const registrarRun = (dados: { loja: string; faturamento: string }): number => {
    const run = demoRuns + 1;
    setDemoRuns(run);
    setLeadMemo((m) => ({
      loja: dados.loja,
      faturamento: dados.faturamento,
      nome: m?.nome ?? "",
      email: m?.email ?? "",
      whatsapp: m?.whatsapp ?? "",
    }));
    return run;
  };

  /** Captura do lead (oferta enviada): guarda contato p/ pré-fill + marca o flag. */
  const registrarLead = (dados: { nome: string; email: string; whatsapp: string }): void => {
    setLeadMemo((m) => ({
      loja: m?.loja ?? persona?.loja ?? "",
      faturamento: m?.faturamento ?? persona?.faturamento ?? "",
      nome: dados.nome,
      email: dados.email,
      whatsapp: dados.whatsapp,
    }));
    setLeadCapturado(true);
  };

  // Liga/desliga a persona no client (tenant ativo) + registro sintético. Mesmo
  // mecanismo da impersonação: trocar o tenant re-escopa todas as queries. No
  // boot/reload, session.tsx faz isto a nível de módulo (antes do 1º render).
  const aplicarPersona = (p: DemoPersona | null) => {
    if (p?.ativa) {
      setDemoPersona(buildDemoPersonaEntry({ loja: p.loja, sizeFactor: p.sizeFactor }));
      setActiveTenant(DEMO_LEAD_TENANT);
    } else {
      setDemoPersona(null);
      setActiveTenant(DEMO_TENANT);
    }
  };

  const setPersona = (p: DemoPersona | null) => {
    aplicarPersona(p);
    setPersonaState(p);
  };

  const resetDemo = () => {
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
    setTrial(DEFAULTS.trial);
    setUser(DEFAULTS.user);
    setTeam(DEFAULTS.team);
    setMediaConn(DEFAULTS.mediaConn);
    setPersonaState(DEFAULTS.persona);
    setLeadMemo(DEFAULTS.leadMemo);
    setDemoRuns(DEFAULTS.demoRuns);
    setLeadCapturado(DEFAULTS.leadCapturado);
    setDemoFase("gate");
    aplicarPersona(null);
  };

  return (
    <DemoContext.Provider
      value={{
        trial,
        setTrial,
        user,
        setUser,
        team,
        setTeam,
        mediaConn,
        setMediaConn,
        persona,
        setPersona,
        demoFase,
        setDemoFase,
        ofertaOrigem,
        setOfertaOrigem,
        leadMemo,
        demoRuns,
        registrarRun,
        registrarLead,
        leadCapturado,
        resetDemo,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDemo(): DemoContextValue {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo precisa estar dentro de <DemoProvider>");
  return ctx;
}

/** Lê a persona persistida (lucraa_demo_v1) FORA do React — usada pelo
 *  session.tsx a nível de módulo para re-escopar o tenant antes do 1º render
 *  (mesmo motivo da impersonação: efeitos rodam filho→pai, tarde demais). */
// eslint-disable-next-line react-refresh/only-export-components
export function readPersistedPersona(): DemoPersona | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const p = (JSON.parse(raw) as Partial<DemoState>).persona;
    return p && p.ativa ? p : null;
  } catch {
    return null;
  }
}
